import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import "./MemoeryGame.css";
import {
  addMemoryGameSkill,
  clearAddMemoryGameSkillResponse,
} from "../../Redux/Slices/MemoryGameSkill/AddMemoryGameSkillSlice";
import { useAppSelector, useAppDispatch } from "../../Redux/Store/Hooks";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  TextField,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useNavigate } from "react-router-dom";
import PauseIcon from "@mui/icons-material/Pause";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Target from "./GamepageImage/target-05.svg";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import Tick from "./GamepageImage/check-circle.svg";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import Trophy from "./GamepageImage/trophy-01.svg";

// Define the possible states of the game for clearer logic
type GameState = "welcome" | "displaying" | "input" | "result";

const MemoryGame: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const nav = useNavigate();

  // Refs for managing intervals
  const displayIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inputTimerRef = useRef<NodeJS.Timeout | null>(null);

  // --- State Management ---
  const [gameState, setGameState] = useState<GameState>("welcome");
  const [level, setLevel] = useState(1);
  const [selectedStep, setSelectedStep] = useState(0); // 0: Number, 1: Letter, 2: Word
  const [timeLeft, setTimeLeft] = useState(30);
  const [score, setScore] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [sequence, setSequence] = useState<string[]>([]);
  const [userInput, setUserInput] = useState<string[]>([]); // Use a single string for input
  const [gameOver, setGameOver] = useState(false);
  const [showCurrentItem, setShowCurrentItem] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [currentSequenceIndex, setCurrentSequenceIndex] = useState(0);
  const [hasScoredThisRound, setHasScoredThisRound] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [paused, setPaused] = useState(false);

  // New state for managing display sequence
  const [displayProgress, setDisplayProgress] = useState(0);
  const [totalDisplayItems, setTotalDisplayItems] = useState(0);

  const { data: addMemoryGameSkills } = useAppSelector(
    (state) => state.AddMemoryGameSkillSlice
  );
  const Datamemory: any = addMemoryGameSkills?.data;
  const Datamemorystatus: any = addMemoryGameSkills?.status;

  const rounds = [
    {
      title: "Number Memory",
      desc: "Memorize the number sequence",
      color: "#4ade80",
      step: "1",
      maxLevel: 3,
    },
    {
      title: "Letter Memory",
      desc: "Memorize the letter sequence",
      color: "#60a5fa",
      step: "2",
      maxLevel: 6,
    },
    {
      title: "Word Order Memory",
      desc: "Memorize the word sequence",
      color: "#c084fc",
      step: "3",
      maxLevel: 9,
    },
  ];

  // Get level limitations based on selected step
  const getLevelLimitations = (step: number): { min: number; max: number } => {
    switch (step) {
      case 0:
        return { min: 1, max: 25 }; // Numbers: 1-25
      case 1:
        return { min: 1, max: 25 }; // Letters: 1-25
      case 2:
        return { min: 1, max: 25 }; // Words: 1-25
      default:
        return { min: 1, max: 25 };
    }
  };

  // Check if current level exceeds the limit for selected step
  const isLevelCompleted = () => {
    const limitations = getLevelLimitations(selectedStep);
    return level > limitations.max;
  };

  // --- Game Flow Handlers ---

  // Clear all intervals
  const clearAllIntervals = () => {
    if (displayIntervalRef.current) {
      clearInterval(displayIntervalRef.current);
      displayIntervalRef.current = null;
    }
    if (inputTimerRef.current) {
      clearInterval(inputTimerRef.current);
      inputTimerRef.current = null;
    }
  };

  // Starts the game from the welcome screen
  const handleStart = () => {
    const limitations = getLevelLimitations(selectedStep);
    setScore(0);
    setMistakes(0);
    setGameOver(false);
    setLevel(limitations.min); // Start from the minimum level for selected step
    setUserInput([]);
    setGameState("displaying");
    setPaused(false);
    setProgressValue(0);
    setDisplayProgress(0);
    clearAllIntervals();
    dispatch(clearAddMemoryGameSkillResponse());
    dispatch(
      addMemoryGameSkill({
        level: limitations.min,
        item_type: rounds[selectedStep].title.split(" ")[0].toLowerCase(), // Use the first word of the title as item_type
      })
    );
  };

  // Displays the sequence one item at a time (no pause during display)
  const startDisplayingSequence = useCallback((items: string[]) => {
    setSequence(items);
    setTotalDisplayItems(items.length);
    setDisplayProgress(0);
    setPaused(false); // Ensure not paused during sequence display

    let i = 0;
    const interval = setInterval(() => {
      if (i < items.length) {
        setShowCurrentItem(items[i]);
        setCurrentSequenceIndex(i);
        setDisplayProgress(i + 1);
        setProgressValue(((i + 1) / items.length) * 100);
        i++;
      } else {
        clearInterval(interval);
        displayIntervalRef.current = null;
        setShowCurrentItem(null);
        setTimeout(() => {
          setGameState("input");
          setTimeLeft(30);
        }, 2000);
      }
    }, 1000);

    displayIntervalRef.current = interval;
  }, []);

  useEffect(() => {
    if (
      gameState === "displaying" &&
      Datamemorystatus &&
      Datamemory?.grid?.length > 0
    ) {
      setHasScoredThisRound(false);
      startDisplayingSequence(Datamemory.grid);
    }
  }, [gameState, Datamemorystatus, Datamemory?.grid, startDisplayingSequence]);

  // Effect for the input countdown timer with pause functionality
  useEffect(() => {
    if (gameState !== "input" || gameOver || paused) {
      if (inputTimerRef.current) {
        clearInterval(inputTimerRef.current);
        inputTimerRef.current = null;
      }
      return;
    }

    if (timeLeft <= 0) {
      handleTimeUp();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    inputTimerRef.current = timer;
    return () => {
      clearInterval(timer);
      inputTimerRef.current = null;
    };
  }, [gameState, timeLeft, gameOver, paused]);

  // Checks the user's answer
  const checkAnswer = () => {
    if (paused) return;

    // Join words with a space, otherwise join with no space
    const joiner = selectedStep === 2 ? " " : "";

    const correctSequence = sequence.join(joiner).toLowerCase();
    const userSequence = userInput.join(joiner).toLowerCase();

    const isAnswerCorrect = userSequence === correctSequence;

    setIsCorrect(isAnswerCorrect);

    if (isAnswerCorrect) {
      if (!hasScoredThisRound) {
        setScore((prev) => prev + 100);
        setHasScoredThisRound(true);
      }
    } else {
      const newMistakes = mistakes + 1;
      setMistakes(newMistakes);
      if (newMistakes >= 3) {
        setGameOver(true);
      }
    }
    clearAllIntervals();
    setGameState("result");
  };

  // Called when the timer runs out
  const handleTimeUp = () => {
    setIsCorrect(false);
    const newMistakes = mistakes + 1;
    setMistakes(newMistakes);
    if (newMistakes >= 3) {
      setGameOver(true);
    }
    clearAllIntervals();
    setGameState("result");
  };

  const handleNextRound = () => {
    if (isLevelCompleted()) {
      setGameOver(true);
      return;
    }

    const nextLevel = level + 1;
    setLevel(nextLevel);
    setUserInput([]);
    setPaused(false);
    setProgressValue(0);
    setDisplayProgress(0);
    clearAllIntervals();
    dispatch(clearAddMemoryGameSkillResponse());
    dispatch(
      addMemoryGameSkill({
        level: nextLevel,
        item_type: rounds[selectedStep].title.split(" ")[0].toLowerCase(),
      })
    );
    setGameState("displaying");
  };

  const handleTryAgain = () => {
    setUserInput([]);
    setPaused(false);
    setProgressValue(0);
    setDisplayProgress(0);
    clearAllIntervals();
    dispatch(clearAddMemoryGameSkillResponse());
    dispatch(
      addMemoryGameSkill({
        level: level,
        item_type: rounds[selectedStep].title.split(" ")[0].toLowerCase(),
      })
    );
    setGameState("displaying");
  };

  // Resets the entire game after "Game Over"
  const handleRetryGame = () => {
    handleStart();
  };

  const handleBackToSelection = () => {
    clearAllIntervals();
    setPaused(false);
    setGameState("welcome");
  };

  const handleResume = () => {
    setPaused(false);
  };

  const handlePause = () => {
    // Only allow pause during input phase
    if (gameState === "input") {
      setPaused(true);
      clearAllIntervals();
    }
  };

  const handleBack = () => {
    clearAllIntervals();
    nav("/skillstrainer");
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        if (gameState === "input" && userInput.length > 0 && !paused) {
          checkAnswer();
        } else if (gameState === "welcome" || gameState === "result") {
          handleStart();
        }
      }

      if (e.key === "Escape") {
        if (gameState === "input") {
          if (paused) {
            handleResume();
          } else {
            handlePause();
          }
        }
      }
    };
    if (
      userInput?.length === sequence?.length &&
      userInput.every((v) => v?.trim())
    ) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameState, userInput, paused, checkAnswer, handleStart, handleTryAgain]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, []);

  return (
    <Box
      sx={{ padding: "20px 20px", display: "flex", justifyContent: "center" }}
    >
      <Box sx={{ maxWidth: "896px", width: "100%" }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mb={2}
          sx={{
            flexWrap: "wrap",
            gap: "10px 30px",
          }}
        >
          <Button
            onClick={
              gameState === "welcome" ? handleBack : handleBackToSelection
            }
            sx={{ color: "#4b5563", whiteSpace: "nowrap", marginLeft: "-15px" }}
          >
            <ArrowBackIcon />
            <span className="txt_hid">
              {gameState === "welcome"
                ? "Back to Skills Trainer"
                : "Back to Selection"}
            </span>
          </Button>
          <Box textAlign="center">
            <Typography className="arithmetic-title">
              Memory Challenge
            </Typography>
            <Typography className="arithmetic-subtitle">
              Round {rounds[selectedStep].step}: {rounds[selectedStep].title}
            </Typography>
          </Box>
          <Box
            textAlign="right"
            display="flex"
            flexDirection="row"
            alignItems="flex-end"
            justifyContent="space-around"
          >
            {gameState !== "welcome" && !gameOver && gameState !== "result" && (
              <>
                <Box sx={{ textAlign: "right" }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    gap={1}
                    sx={{ flexWarp: "wrap" }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      flexDirection="row"
                    >
                      <img
                        src={Target}
                        alt="Trophy"
                        style={{
                          width: "16px",
                          height: "16px",
                          verticalAlign: "middle",
                          marginRight: "8px",
                        }}
                      />
                      <Typography>Score {score}</Typography>
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      flexDirection="row"
                      gap={"5px"}
                    >
                      <CancelOutlinedIcon />
                      <Typography>Mistakes {mistakes} / 3</Typography>
                    </Box>
                    <Button
                      onClick={paused ? handleResume : handlePause}
                      className="icon-button green-hover"
                      disabled={gameState === "displaying"} // Disable during sequence display
                    >
                      {paused ? <PlayArrowIcon /> : <PauseIcon />}
                    </Button>

                    <Button
                      onClick={handleTryAgain}
                      className="icon-button green-hover"
                    >
                      <RestartAltIcon />
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Welcome Screen */}
        {gameState === "welcome" && (
          <>
            <Box className="step-cards" mt={4}>
              {rounds.map((round, index) => {
                const limitations = getLevelLimitations(index);
                return (
                  <Box
                    key={index}
                    className={`step-card ${
                      selectedStep === index ? "active" : ""
                    }`}
                    onClick={() => setSelectedStep(index)}
                  >
                    <Box
                      className="step-circle"
                      style={{ backgroundColor: round.color }}
                    >
                      {round.step}
                    </Box>
                    <Typography className="step-title">
                      {round.title}
                    </Typography>
                    <Typography className="step-desc">{round.desc}</Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                    ></Typography>
                  </Box>
                );
              })}
            </Box>
            <Box className="main-card" mt={4}>
              {rounds[selectedStep] && (
                <>
                  <Box
                    className="step-circle1"
                    style={{ backgroundColor: rounds[selectedStep].color }}
                  >
                    {rounds[selectedStep].step}
                  </Box>
                  <Typography className="step-title">
                    {rounds[selectedStep].title}
                  </Typography>
                  <Typography className="step-desc">
                    {rounds[selectedStep].desc}
                  </Typography>
                </>
              )}
              <Button
                className="start-btn"
                onClick={handleStart}
                disabled={selectedStep === null || selectedStep === undefined}
                sx={{ mt: 2 }}
              >
                <PlayArrowIcon sx={{ mr: 1 }} /> Start Round (Enter)
              </Button>
            </Box>
          </>
        )}

        {gameState === "displaying" && (
          <>
            <Typography sx={{ mt: 4, mb: 1, textAlign: "center" }}>
              Memorize Sequence
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                my: 3,
                height: 10,
                borderRadius: 5,
                backgroundColor: "#eee",
                overflow: "hidden", // Ensures rounded corners show correctly
                "& .MuiLinearProgress-bar": {
                  backgroundImage:
                    timeLeft > 1
                      ? "linear-gradient(90deg, #4ade80 , #22c55e )" // Green gradient
                      : timeLeft > 0
                      ? "linear-gradient(90deg, #facc15 , #f97316 )" // Orange to Red
                      : "linear-gradient(90deg,  #f87171, #ef4444 )", // Deep red gradient
                  backgroundColor: "transparent", // prevent override
                },
              }}
            />

            <Box textAlign="center" mt={6} minHeight="150px">
              {showCurrentItem !== null ? (
                <>
                  <Typography
                    variant="h1"
                    sx={{
                      fontWeight: "bold",
                      color: "text.primary",
                      opacity: 1,
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    {showCurrentItem}
                  </Typography>
                  <Typography color="textSecondary">
                    Memorize this item
                  </Typography>
                </>
              ) : (
                <Typography variant="h6" color="textSecondary">
                  Memorize this item
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* Input Screen */}
        {gameState === "input" && !gameOver && (
          <>
            <Box
              mt={4}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>Answer</Typography>
              <Typography color={timeLeft <= 10 ? "error" : "textPrimary"}>
                <AccessTimeIcon sx={{ verticalAlign: "middle" }} />
                {paused ? "Paused" : timeLeft}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={paused ? 100 : (timeLeft / 30) * 100}
              sx={{
                my: 3,
                height: 10,
                borderRadius: 5,
                backgroundColor: "#eee",
                overflow: "hidden", // Ensures rounded corners show correctly
                "& .MuiLinearProgress-bar": {
                  backgroundImage:
                    timeLeft > 10
                      ? "linear-gradient(90deg, #4ade80 , #22c55e )" // Green gradient
                      : timeLeft > 5
                      ? "linear-gradient(90deg, #facc15 , #f97316 )" // Orange to Red
                      : "linear-gradient(90deg,  #f87171, #ef4444 )", // Deep red gradient
                  backgroundColor: "transparent", // prevent override
                },
              }}
            />
            <Box textAlign="center" mt={4}>
              <Box sx={{ padding: "48px 0px" }}>
                <Typography variant="h6">
                  {paused ? (
                    <>
                      <Typography
                        variant="h3"
                        color="primary"
                        sx={{ mb: 2, fontWeight: "700", color: "#374151" }}
                      >
                        Game Paused
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleResume}
                        sx={{
                          mr: 2,
                          fontWeight: "700",
                          color: "white",
                          textTransform: "none",
                          fontSize: "18px",
                        }}
                        size="large"
                      >
                        Resume Game
                      </Button>
                    </>
                  ) : (
                    <Typography fontSize={"20px"} sx={{ fontWeight: "600" }}>
                      Enter the sequence you saw:
                    </Typography>
                  )}
                </Typography>

                {!paused && (
                  <>
                    <Box className="gamemath-box" textAlign="center">
                      <div className="Solutions_memory">
                        {sequence &&
                          sequence.map((_, index) => (
                            <TextField
                              key={index}
                              type={
                                rounds[selectedStep].title === "Number Memory"
                                  ? "number"
                                  : "text"
                              }
                              variant="outlined"
                              autoFocus={
                                index === 0 && userInput.every((val) => !val)
                              }
                              placeholder={`Enter value ${index + 1}`}
                              value={userInput[index] || ""}
                              onChange={(e) => {
                                const updated = [...userInput];
                                updated[index] = e.target.value;
                                setUserInput(updated);
                              }}
                              inputProps={{
                                onKeyDown:
                                  rounds[selectedStep].title === "Number Memory"
                                    ? (
                                        e: React.KeyboardEvent<HTMLInputElement>
                                      ) => {
                                        if (
                                          ["e", "E", "+", "-"].includes(e.key)
                                        ) {
                                          e.preventDefault();
                                        }
                                      }
                                    : undefined,
                              }}
                              className="answer-inputs"
                              sx={{
                                minWidth: "200px",
                                padding: "0 10px",
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                },
                                "& input[type=number]::-webkit-outer-spin-button":
                                  {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                                "& input[type=number]::-webkit-inner-spin-button":
                                  {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                  },
                              }}
                              // inputProps={{ style: { textAlign: "center" } }}
                            />
                          ))}
                      </div>
                      <Button
                        className="submit-btn"
                        variant="contained"
                        onClick={checkAnswer}
                        disabled={
                          userInput?.length < sequence?.length ||
                          userInput.some((v) => !v?.trim())
                        }
                      >
                        Submit (Enter)
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </>
        )}

        {/* Result Screen */}
        {gameState === "result" && !gameOver && (
          <Box className="text-center mt-6">
            <Box
              className="games-over"
              sx={{ borderColor: isCorrect ? "success.main" : "error.main" }}
            >
              <Box sx={{ padding: "48px 0px" }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: isCorrect ? "success.main" : "error.main",
                  }}
                >
                  {isCorrect ? (
                    <Box>
                      <Box
                        className="step-circle1"
                        style={{ backgroundColor: "#dcfce7" }}
                      >
                        <Typography>
                          <img
                            src={Tick}
                            alt="Trophy"
                            style={{
                              width: "32px",
                              height: "32px",
                              verticalAlign: "middle",
                            }}
                          />
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "24px",
                          fontWeight: "700",
                          color: "#4caf50",
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={Tick}
                          alt="Trophy"
                          style={{
                            width: "24px",
                            height: "24px",
                            verticalAlign: "middle",
                          }}
                        />
                        Correct!+ 100 Points
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box
                        className="step-circle1"
                        style={{ backgroundColor: "#fee2e2" }}
                      >
                        <Typography>
                          <CancelOutlinedIcon
                            style={{
                              width: "32px",
                              height: "32px",
                              verticalAlign: "middle",
                              color: "red",
                            }}
                          />
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "24px",
                          fontWeight: "700",
                          color: "#dc2626",
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CancelOutlinedIcon
                          style={{
                            width: "24px",
                            height: "24px",
                            verticalAlign: "middle",
                          }}
                        />
                        Incorrect
                      </Typography>
                      <Typography></Typography>
                    </Box>
                  )}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#4b5563",
                    fontWeight: "500",
                    marginBottom: "20px",
                  }}
                >
                  Correct answer: {sequence.join(selectedStep === 2 ? " " : "")}
                </Typography>
                <Box className="Result_retry_button">
                  <Button
                    variant="contained"
                    className="tryagain-btn"
                    onClick={handleTryAgain}
                  >
                    <RestartAltIcon /> Try Again(Enter)
                  </Button>

                  <Button
                    variant="contained"
                    className="nextround-btn"
                    onClick={handleNextRound}
                    disabled={isLevelCompleted()}
                  >
                    <ElectricBoltIcon /> Next Round
                  </Button>

                  <Button
                    variant="contained"
                    className="nextround-btn"
                    onClick={handleBackToSelection}
                  >
                    Back to Selection
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Game Over Screen */}
        {gameState !== "welcome" && gameOver && (
          <Box
            textAlign="center"
            sx={{
              background: "#f9ffff",
              borderRadius: "10px",
              boxShadow: 3,
              padding: "48px",
            }}
          >
            <Typography
              variant="h3"
              fontWeight={700}
              color="primary"
              gutterBottom
            >
              <img
                src={Trophy}
                alt="gameover"
                style={{
                  width: "96px",
                  height: "96px",
                  verticalAlign: "middle",
                  marginRight: "8px",
                }}
              />
            </Typography>

            <Typography
              variant="h3"
              fontWeight={700}
              className="ResultTitle"
              gutterBottom
            >
              Training Complete!
            </Typography>

            <Typography variant="h5" mt={2} fontWeight={700} fontSize={"18px"}>
              Your final score: {score}
            </Typography>

            <Grid container justifyContent="center" spacing={2} mt={2}>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleStart}
                  sx={{
                    backgroundColor: "#0055ff",
                    "&:hover": { backgroundColor: "#0055ff" },
                    fontSize: "15px",
                    gap: "5px",
                    color: "white",
                    width: "250px",
                  }}
                >
                  <RestartAltIcon /> Play Again (Enter)
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{
                    fontSize: "15px",
                    backgroundColor: "white",
                    "&:hover": {
                      backgroundColor: "#4caf50",
                      color: "white",
                      width: "250px",
                    },
                  }}
                >
                  Back to Skills Trainer
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {!gameOver && (
          <Box className="shortcuts" textAlign="center" mt={4}>
            <Typography>
              <strong>Shortcuts:</strong> <kbd>Enter</kbd> Start/Submit &nbsp;
              {gameState === "input" && (
                <>
                  <kbd>Esc</kbd> {paused ? "Resume" : "Pause"} &nbsp;
                </>
              )}
              {gameState !== "displaying" && (
                <>
                  <kbd>Backspace</kbd> ClearInput
                </>
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MemoryGame;
